import { useState } from "react";
import styled from "styled-components";

import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import {
  UserAnnualIncome,
  UserEmploymentStatus,
  UserLiquidNetWorth,
  UserSourceOfFunds,
} from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { entryColors, entryFont } from "@entry/tokens";
import { GlassSelect } from "@entry/ui";
import { useErrorBanner, useOnboarding, useSetUserFinancialProfile, useUser } from "@hooks";
import { i18n, Translations } from "@i18n";
import { AssetRangeDisclaimer } from "./AssetRangeDisclaimer";

const LIQUID_NET_WORTH_OPTIONS = [
  {
    text: i18n.t(Translations.AssetRangeRouteLessThan100000),
    type: UserLiquidNetWorth.LessThan_100000,
  },
  {
    text: i18n.t(Translations.AssetRangeRouteBetween100000And250000),
    type: UserLiquidNetWorth.Between_100000And_250000,
  },
  {
    text: i18n.t(Translations.AssetRangeRouteBetween250000And500000),
    type: UserLiquidNetWorth.Between_250000And_500000,
  },
  {
    text: i18n.t(Translations.AssetRangeRouteMoreThan500000),
    type: UserLiquidNetWorth.MoreThan_500000,
  },
];

const LIQUID_NET_WORTH_OPTIONS_TEXT = LIQUID_NET_WORTH_OPTIONS.map((option) => option.text);

const ANNUAL_INCOME_OPTIONS = [
  {
    text: i18n.t(Translations.AssetRangeRouteAnnualIncome0To100000),
    type: UserAnnualIncome.LessThan_100000,
  },
  {
    text: i18n.t(Translations.AssetRangeRouteAnnualIncome100000To250000),
    type: UserAnnualIncome.Between_100000And_250000,
  },
  {
    text: i18n.t(Translations.AssetRangeRouteAnnualIncome250000To500000),
    type: UserAnnualIncome.Between_250000And_500000,
  },
  {
    text: i18n.t(Translations.AssetRangeRouteAnnualIncome500000To10000000),
    type: UserAnnualIncome.MoreThan_500000,
  },
];

const ANNUAL_INCOME_OPTIONS_TEXT = ANNUAL_INCOME_OPTIONS.map((option) => option.text);

const FUNDING_SOURCE_OPTIONS = [
  {
    text: i18n.t(Translations.AccountFundingSourceRouteEmploymentIncome),
    type: UserSourceOfFunds.EmploymentIncome,
  },
  {
    text: i18n.t(Translations.AccountFundingSourceRouteInvestments),
    type: UserSourceOfFunds.Investments,
  },
  {
    text: i18n.t(Translations.AccountFundingSourceRouteInheritance),
    type: UserSourceOfFunds.Inheritance,
  },
  {
    text: i18n.t(Translations.AccountFundingSourceRouteBusinessIncome),
    type: UserSourceOfFunds.BusinessIncome,
  },
  {
    text: i18n.t(Translations.AccountFundingSourceRouteSavings),
    type: UserSourceOfFunds.Savings,
  },
  {
    text: i18n.t(Translations.AccountFundingSourceRouteFamily),
    type: UserSourceOfFunds.Family,
  },
];

const FUNDING_SOURCE_OPTIONS_TEXT = FUNDING_SOURCE_OPTIONS.map((option) => option.text);

const getDefaultFundingSourceIndex = (employmentStatus?: UserEmploymentStatus | null): number => {
  const isEmployed = Boolean(
    employmentStatus &&
    [UserEmploymentStatus.Employed, UserEmploymentStatus.SelfEmployed].includes(employmentStatus),
  );
  const type = isEmployed ? UserSourceOfFunds.EmploymentIncome : UserSourceOfFunds.Savings;
  return FUNDING_SOURCE_OPTIONS.findIndex((o) => o.type === type);
};

const Footer = styled.p`
  margin: 0;
  font-family: ${entryFont.body};
  font-size: 12px;
  line-height: 1.5;
  color: ${entryColors.textSecondary};
  text-align: center;
`;

export const AssetRangeRoute = () => {
  const { user } = useUser();
  const defaultFundingSourceIndex = getDefaultFundingSourceIndex(
    user?.userFinancialProfile?.employmentStatus,
  );

  const [netWorthIndex, setNetWorthIndex] = useState(0);
  const [annualIncomeIndex, setAnnualIncomeIndex] = useState(0);
  const [fundingSourceIndex, setFundingSourceIndex] = useState(defaultFundingSourceIndex);

  const [setUserFinancialProfile, { loading }] = useSetUserFinancialProfile();
  const { showErrorBanner } = useErrorBanner();
  const { resetToNext } = useOnboarding();

  const handleSubmit = async () => {
    try {
      await setUserFinancialProfile({
        variables: {
          liquidNetWorth: LIQUID_NET_WORTH_OPTIONS[netWorthIndex].type,
          annualIncome: ANNUAL_INCOME_OPTIONS[annualIncomeIndex].type,
          sourceOfFunds: FUNDING_SOURCE_OPTIONS[fundingSourceIndex]?.type,
        },
      });

      resetToNext();
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.AssetRangeRouteTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.AssetRangeRoutePrimaryCTA),
        onClick: handleSubmit,
        loading,
        disabled: netWorthIndex == null || annualIncomeIndex == null || fundingSourceIndex < 0,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <GlassSelect
        label={i18n.t(Translations.AssetRangeRouteInputLabel)}
        placeholder={i18n.t(Translations.AssetRangeRouteInputPlaceholder)}
        items={LIQUID_NET_WORTH_OPTIONS_TEXT}
        selectedIndex={netWorthIndex}
        onSelect={setNetWorthIndex}
      />
      <Spacing size={SpacingSizes.M} />

      <GlassSelect
        label={i18n.t(Translations.AssetRangeRouteAnnualIncomeLabel)}
        placeholder={i18n.t(Translations.AssetRangeRouteAnnualIncomePlaceholder)}
        items={ANNUAL_INCOME_OPTIONS_TEXT}
        selectedIndex={annualIncomeIndex}
        onSelect={setAnnualIncomeIndex}
      />
      <Spacing size={SpacingSizes.M} />

      <GlassSelect
        label={i18n.t(Translations.AccountFundingSourceRouteInputLabel)}
        items={FUNDING_SOURCE_OPTIONS_TEXT}
        selectedIndex={fundingSourceIndex}
        onSelect={setFundingSourceIndex}
      />
      <Spacing size={SpacingSizes.M} />
      <AssetRangeDisclaimer />

      <Spacing size={SpacingSizes.M} />
      <Footer>{i18n.t(Translations.AssetRangeRouteDisclaimerFooter)}</Footer>
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
